import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiArrowLeft, FiSave, FiAlertCircle } from "react-icons/fi";
import toast from "react-hot-toast";
import { getPricingRuleByIdThunk } from "../../../store/thunks/pricingThunks";
import {
  selectCurrentPricingRule,
  selectPricingRulesLoading,
} from "../../../store/slices/pricingSlice";
import { usePricingRuleForm } from "./pricingRuleFormHooks";
import Button from "../../../components/common/Button";
import {
  BasicInfoSection,
  ConditionsSection,
  ChargesSection,
  GSTConfigSection,
  ValiditySection,
} from "./PricingRuleFormSections";
import { getAllCategoriesThunk } from "store/thunks/categoryThunks";
import { getBranchesThunk } from "store/thunks/branchThunks";

const PricingRuleForm = ({ mode = "add" }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedRule = useSelector(selectCurrentPricingRule);
  const loading = useSelector(selectPricingRulesLoading);
  const { branches } = useSelector((state) => state.branch);
  const { categories } = useSelector((state) => state.category);

  // Custom form hook
  const {
    formData,
    errors,
    submitting,
    activeSection,
    setActiveSection,
    isDataLoaded,
    handleChange,
    handleArrayChange,
    handleSlabChange,
    handleAddSlab,
    handleRemoveSlab,
    handleTierChange,
    handleAddTier,
    handleRemoveTier,
    handleAdditionalChargeChange,
    handleAddAdditionalCharge,
    handleRemoveAdditionalCharge,
    handleSubmit,
    validateForm,
  } = usePricingRuleForm(selectedRule, mode);

  // Load data for edit/clone mode
  useEffect(() => {
    if ((mode === "edit" || mode === "clone") && id) {
      dispatch(getPricingRuleByIdThunk(id));
    }
  }, [mode, id, dispatch]);

  // Load categories
  useEffect(() => {
    dispatch(getAllCategoriesThunk());
  }, [dispatch]);

  // Load branches
  useEffect(() => {
    dispatch(getBranchesThunk({ isActive: true }));
  }, [dispatch]);

  // Form submission
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      // Scroll to first error
      const firstErrorSection = Object.keys(errors)[0]?.split(".")[0];
      if (firstErrorSection) {
        const sectionMap = {
          name: "basic",
          priority: "basic",
          conditions: "conditions",
          charges: "charges",
          gstConfig: "gst",
          validFrom: "validity",
          validUntil: "validity",
        };
        setActiveSection(sectionMap[firstErrorSection] || "basic");
      }
      return;
    }
    await handleSubmit();
  };

  // Loading state for edit/clone mode
  if ((mode === "edit" || mode === "clone") && !isDataLoaded) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-neutral-dark">Loading pricing rule data...</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (
    (mode === "edit" || mode === "clone") &&
    !loading &&
    !selectedRule &&
    id
  ) {
    return (
      <div className="container mx-auto p-4 md:p-6">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FiAlertCircle className="mx-auto text-5xl text-warning mb-4" />
          <h2 className="text-2xl font-bold text-text mb-2">
            Pricing Rule Not Found
          </h2>
          <p className="text-neutral-dark mb-6">
            The pricing rule you're trying to {mode} doesn't exist.
          </p>
          <Button onClick={() => navigate("/pricing-rules")}>
            <FiArrowLeft className="mr-2" />
            Back to Pricing Rules
          </Button>
        </div>
      </div>
    );
  }

  // Navigation sections
  const sections = [
    {
      id: "basic",
      label: "Basic Info",
      hasErrors: errors.name || errors.priority,
    },
    {
      id: "conditions",
      label: "Conditions",
      hasErrors: Object.keys(errors).some((k) => k.startsWith("conditions.")),
    },
    {
      id: "charges",
      label: "Charges",
      hasErrors: Object.keys(errors).some((k) => k.startsWith("charges.")),
    },
    {
      id: "gst",
      label: "GST Config",
      hasErrors: Object.keys(errors).some((k) => k.startsWith("gstConfig.")),
    },
    {
      id: "validity",
      label: "Validity",
      hasErrors: errors.validFrom || errors.validUntil,
    },
  ];

  const getPageTitle = () => {
    switch (mode) {
      case "add":
        return "Create Pricing Rule";
      case "edit":
        return "Edit Pricing Rule";
      case "clone":
        return "Clone Pricing Rule";
      default:
        return "Pricing Rule";
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-6">
      <form onSubmit={onSubmit} className="space-y-6 pb-24">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            type="button"
            onClick={() => navigate("/pricing-rules")}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiArrowLeft size={24} />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-text">
              {getPageTitle()}
            </h1>
            {(mode === "edit" || mode === "clone") && selectedRule && (
              <p className="text-sm text-neutral-dark mt-1">
                {mode === "clone" ? "Cloning from: " : "Editing: "}
                {selectedRule.name}
              </p>
            )}
          </div>
          {/* Error count badge */}
          {Object.keys(errors).length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-danger-light text-danger-dark rounded-lg">
              <FiAlertCircle />
              <span className="text-sm font-medium">
                {Object.keys(errors).length} error
                {Object.keys(errors).length > 1 ? "s" : ""}
              </span>
            </div>
          )}
        </div>

        {/* Section Navigation */}
        <div className="bg-white rounded-lg shadow p-2 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {sections.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveSection(section.id)}
                className={`relative px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                  activeSection === section.id
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-neutral-dark hover:bg-gray-200"
                }`}
              >
                {section.label}
                {section.hasErrors && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-danger rounded-full" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Form Sections */}
        <div className="space-y-6">
          {activeSection === "basic" && (
            <BasicInfoSection
              formData={formData}
              errors={errors}
              onChange={handleChange}
              mode={mode}
            />
          )}

          {activeSection === "conditions" && (
            <ConditionsSection
              formData={formData}
              errors={errors}
              onChange={handleChange}
              onArrayChange={handleArrayChange}
              branches={branches}
              categories={categories}
            />
          )}

          {activeSection === "charges" && (
            <ChargesSection
              formData={formData}
              errors={errors}
              onChange={handleChange}
              onSlabChange={handleSlabChange}
              onAddSlab={handleAddSlab}
              onRemoveSlab={handleRemoveSlab}
              onTierChange={handleTierChange}
              onAddTier={handleAddTier}
              onRemoveTier={handleRemoveTier}
              onAdditionalChargeChange={handleAdditionalChargeChange}
              onAddAdditionalCharge={handleAddAdditionalCharge}
              onRemoveAdditionalCharge={handleRemoveAdditionalCharge}
            />
          )}

          {activeSection === "gst" && (
            <GSTConfigSection
              formData={formData}
              errors={errors}
              onChange={handleChange}
            />
          )}

          {activeSection === "validity" && (
            <ValiditySection
              formData={formData}
              errors={errors}
              onChange={handleChange}
            />
          )}
        </div>

        {/* Fixed Bottom Actions */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-10">
          <div className="container mx-auto flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
            <div className="text-sm text-neutral-dark">
              {Object.keys(errors).length > 0 ? (
                <span className="text-danger font-medium">
                  {Object.keys(errors).length} error(s) found - please review
                </span>
              ) : (
                <span className="text-success-dark font-medium">
                  ✓ All fields valid
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/pricing-rules")}
                disabled={submitting}
                className="flex-1 sm:flex-none"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={submitting}
                disabled={submitting}
                className="flex-1 sm:flex-none"
              >
                <FiSave className="mr-2" />
                {submitting
                  ? "Saving..."
                  : mode === "add"
                  ? "Create Rule"
                  : mode === "clone"
                  ? "Clone Rule"
                  : "Update Rule"}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PricingRuleForm;
